import React, { useState } from 'react';
import { X } from 'lucide-react';
import C from '../../constants/colors';

export default function SiteFormModal({ site, companies, employees = [], preselectedCompanyId, onSave, onClose }) {
  const [form, setForm] = useState({
    companyId: site?.companyId || preselectedCompanyId || (companies[0]?.id || ''),
    name: site?.name || '',
    address: site?.address || '',
    lat: site?.lat || '',
    lng: site?.lng || '',
    totalCapacity: site?.totalCapacity || '',
    floors: site?.floors || '',
    safetyOfficerName: site?.safetyOfficerName || '',
    safetyOfficerPhone: site?.safetyOfficerPhone || '',
    security: site?.emgSecurity || '',
    ambulance: site?.emgAmbulance || '',
    police: site?.emgPolice || '',
    civilDefense: site?.emgCivil || '',
    taGeofenceRadiusM: site?.taGeofenceRadiusM ?? 200,
    taViolationMode: site?.taViolationMode || 'HARD_BLOCK',
    taApprovalManagerId: site?.taApprovalManagerId || '',
  });
  const [errors, setErrors] = useState([]);
  const required = ['companyId','name','address','lat','lng'];
  const labels = { companyId:'Company', name:'Site Name', address:'Address', lat:'Latitude', lng:'Longitude' };

  const companyEmployees = employees.filter(e => e.companyId === form.companyId);

  function handleSubmit() {
    const missing = required.filter(k => !String(form[k]).trim());
    if (missing.length) { setErrors(missing.map(k => labels[k])); return; }
    onSave({
      ...(site?.id ? { id: site.id } : {}),
      companyId: form.companyId, name: form.name.trim(), address: form.address.trim(),
      lat: parseFloat(form.lat), lng: parseFloat(form.lng),
      totalCapacity: parseInt(form.totalCapacity) || 0, floors: parseInt(form.floors) || 1,
      safetyOfficerName: form.safetyOfficerName?.trim() || '',
      safetyOfficerPhone: form.safetyOfficerPhone?.trim() || '',
      emgSecurity: form.security.trim(),
      emgAmbulance: form.ambulance.trim(),
      emgPolice: form.police.trim(),
      emgCivil: form.civilDefense.trim(),
      taGeofenceRadiusM: parseInt(form.taGeofenceRadiusM) || 200,
      taViolationMode: form.taViolationMode,
      taApprovalManagerId: form.taApprovalManagerId || null,
    });
    onClose();
  }

  const inp = (key, label, ph='') => (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}{required.includes(key)?' *':''}</label>
      <input value={form[key]} onChange={e => setForm(p => ({...p,[key]:e.target.value}))} placeholder={ph}
        style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
      />
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '28px', width: '540px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: C.textPrimary }}>{site ? 'Edit Site' : 'Add Site'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}><X size={18} /></button>
        </div>
        {errors.length > 0 && <div style={{ background: C.red+'22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: C.red }}>Missing: {errors.join(', ')}</div>}
        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Company *</label>
          <select value={form.companyId} onChange={e => setForm(p => ({...p, companyId: e.target.value, safetyOfficerName: '', safetyOfficerPhone: ''}))}
            style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {inp('name','Site Name','e.g. Dubai HQ')}
        {inp('address','Address','Full address')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {inp('lat','Latitude','25.2048')}{inp('lng','Longitude','55.2708')}
          {inp('totalCapacity','Total Capacity','200')}{inp('floors','Floors','10')}
        </div>
        <div style={{ borderTop: `1px solid ${C.border}`, margin: '16px 0' }} />
        <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Site Safety Officer</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</label>
            <select
              value={form.safetyOfficerName}
              onChange={e => {
                const emp = companyEmployees.find(emp => emp.name === e.target.value);
                setForm(f => ({ ...f, safetyOfficerName: e.target.value, safetyOfficerPhone: emp?.phone || '' }));
              }}
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: form.safetyOfficerName ? C.textPrimary : C.textMuted, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
            >
              <option value="">— Select employee —</option>
              {companyEmployees.map(emp => (
                <option key={emp.id} value={emp.name}>{emp.name}</option>
              ))}
            </select>
          </div>
          {inp('safetyOfficerPhone', 'Phone', '+971509876543')}
        </div>
        <div style={{ borderTop: `1px solid ${C.border}`, margin: '16px 0' }} />
        <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Emergency Numbers</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {inp('security','Security')}{inp('ambulance','Ambulance')}
          {inp('police','Police')}{inp('civilDefense','Civil Defense')}
        </div>
        <div style={{ borderTop: `1px solid ${C.border}`, margin: '16px 0' }} />
        <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Time & Attendance — Geofence</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Geofence Radius (m)</label>
            <input type="number" value={form.taGeofenceRadiusM} onChange={e => setForm(p => ({...p, taGeofenceRadiusM: e.target.value}))} placeholder="200"
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Violation Mode</label>
            <select value={form.taViolationMode} onChange={e => setForm(p => ({...p, taViolationMode: e.target.value}))}
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
              <option value="HARD_BLOCK">Hard Block</option>
              <option value="SOFT_OVERRIDE">Soft Override (self-approve)</option>
              <option value="MANAGER_APPROVAL">Manager Approval</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Approval Manager</label>
          <select value={form.taApprovalManagerId} onChange={e => setForm(p => ({...p, taApprovalManagerId: e.target.value}))}
            style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: form.taApprovalManagerId ? C.textPrimary : C.textMuted, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
            <option value="">— None —</option>
            {companyEmployees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSubmit} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Save Site</button>
        </div>
      </div>
    </div>
  );
}
